// src/components/forms/UnitForm.tsx — create or edit a single home.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useBuildingOptions, usePropertyOptions } from '../../lib/options';
import { Button, Field, Input, Modal, Select } from '../ui';
import { FormError, FormGrid, Wide, csvToList, nulled, numberOr, toMajor, toMinor } from './common';

type U = Record<string, any>;

export const UNIT_TYPES = ['STUDIO', 'APARTMENT', 'DUPLEX', 'TOWNHOUSE', 'VILLA', 'OFFICE', 'RETAIL', 'WAREHOUSE'];
export const UNIT_STATUSES = ['VACANT', 'OCCUPIED', 'NOTICE', 'RESERVED', 'OFF_MARKET', 'UNDER_CONSTRUCTION'];
const label = (s: string) => s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

/**
 * A unit is the atom everything else hangs off — tenancies, rent, work orders — so
 * two fields are deliberately not free-form here: the scheme cannot change after
 * creation, and status moves through its own endpoint so the change is auditable.
 */
export function UnitForm({ open, onClose, unit, propertyId, onSaved }: { open: boolean; onClose: () => void; unit?: U; propertyId?: string; onSaved?: (u: U) => void }) {
  const isNew = !unit;
  const qc = useQueryClient();
  const properties = usePropertyOptions();
  const [form, setForm] = useState(() => ({
    propertyId: unit?.propertyId ?? propertyId ?? '',
    buildingId: unit?.buildingId ?? '',
    reference: unit?.reference ?? '',
    name: unit?.name ?? '',
    floor: unit?.floor == null ? '' : String(unit.floor),
    bedrooms: String(unit?.bedrooms ?? 1),
    bathrooms: String(unit?.bathrooms ?? 1),
    areaSqft: unit?.areaSqft == null ? '' : String(unit.areaSqft),
    unitType: unit?.unitType ?? 'APARTMENT',
    status: unit?.status ?? 'VACANT',
    rent: toMajor(unit?.marketRentMinor),
    currency: unit?.marketRentCurrency ?? 'GBP',
    isFurnished: !!unit?.isFurnished,
    features: (unit?.features ?? []).join(', '),
  }));
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const buildings = useBuildingOptions(form.propertyId);

  const core = () => ({
    buildingId: nulled(form.buildingId),
    reference: form.reference.trim(),
    name: nulled(form.name),
    floor: numberOr(form.floor) ?? null,
    bedrooms: numberOr(form.bedrooms, 1),
    bathrooms: numberOr(form.bathrooms, 1),
    areaSqft: numberOr(form.areaSqft) ?? null,
    unitType: form.unitType,
    marketRentMinor: toMinor(form.rent) ?? 0,
    marketRentCurrency: form.currency,
    isFurnished: form.isFurnished,
    features: csvToList(form.features),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) return (await api.post<U>('/units', { propertyId: form.propertyId, status: form.status, ...core() })).data;
      // Status is a lifecycle move, not an edit: it goes through its own endpoint.
      if (form.status !== unit!.status) await api.patch(`/units/${unit!.id}/status`, { status: form.status });
      return (await api.patch<U>(`/units/${unit!.id}`, core())).data;
    },
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: ['/units'] });
      void qc.invalidateQueries({ queryKey: ['property-avail'] });
      void qc.invalidateQueries({ queryKey: ['options', '/units'] });
      onSaved?.(saved);
      onClose();
    },
  });

  const ready = !!form.propertyId && !!form.reference.trim();

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isNew ? 'New unit' : `Edit ${unit!.reference}`}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => save.mutate()} disabled={!ready || save.isPending}>{save.isPending ? 'Saving…' : isNew ? 'Create unit' : 'Save changes'}</Button>
      </>}
    >
      <div className="space-y-5">
        <FormGrid>
          <Field label="Scheme" hint={isNew ? undefined : 'A unit cannot move between schemes'}>
            <Select value={form.propertyId} disabled={!isNew || !!propertyId} onChange={(e) => set({ propertyId: e.target.value, buildingId: '' })} className="w-full">
              <option value="">Choose a scheme…</option>
              {(properties.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
          <Field label="Building"><Select value={form.buildingId} onChange={(e) => set({ buildingId: e.target.value })} className="w-full" disabled={!form.propertyId}><option value="">None</option>{(buildings.data ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</Select></Field>

          <Field label="Reference" hint="Unique within the scheme"><Input value={form.reference} onChange={(e) => set({ reference: e.target.value })} placeholder="EH-04-12" className="w-full" /></Field>
          <Field label="Display name"><Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Flat 12" className="w-full" /></Field>

          <Field label="Floor"><Input value={form.floor} onChange={(e) => set({ floor: e.target.value })} inputMode="numeric" placeholder="4" className="w-full" /></Field>
          <Field label="Type"><Select value={form.unitType} onChange={(e) => set({ unitType: e.target.value })} className="w-full">{UNIT_TYPES.map((t) => <option key={t} value={t}>{label(t)}</option>)}</Select></Field>

          <Field label="Bedrooms" hint="0 for a studio"><Input value={form.bedrooms} onChange={(e) => set({ bedrooms: e.target.value })} inputMode="numeric" className="w-full" /></Field>
          <Field label="Bathrooms"><Input value={form.bathrooms} onChange={(e) => set({ bathrooms: e.target.value })} inputMode="decimal" className="w-full" /></Field>

          <Field label="Area (sq ft)"><Input value={form.areaSqft} onChange={(e) => set({ areaSqft: e.target.value })} inputMode="numeric" placeholder="720" className="w-full" /></Field>
          <Field label={`Market rent (${form.currency} per month)`}><Input value={form.rent} onChange={(e) => set({ rent: e.target.value })} inputMode="decimal" placeholder="1850" className="w-full" /></Field>

          <Field label="Currency"><Select value={form.currency} onChange={(e) => set({ currency: e.target.value })} className="w-full"><option value="GBP">GBP</option><option value="AED">AED</option></Select></Field>
          <Field label="Status" hint={isNew ? undefined : 'Recorded as a status change on the audit trail'}><Select value={form.status} onChange={(e) => set({ status: e.target.value })} className="w-full">{UNIT_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}</Select></Field>

          <Wide><Field label="Features" hint="Comma separated — Balcony, Ensuite, South facing"><Input value={form.features} onChange={(e) => set({ features: e.target.value })} className="w-full" /></Field></Wide>
          <Wide>
            <label className="flex items-center gap-2.5 text-[13px] text-ink-700">
              <input type="checkbox" checked={form.isFurnished} onChange={(e) => set({ isFurnished: e.target.checked })} className="h-4 w-4 accent-ink-900" /> Furnished
            </label>
          </Wide>
        </FormGrid>

        <FormError error={save.error} />
      </div>
    </Modal>
  );
}
